// Friends, private messages, block and mute, player profiles, friendly
// battles and the training area. Pure rules and message shapes shared by
// server, client and tests; storage lives in server/social.ts.
//
// Friendly battles and training use the ranked arena's engine and rules
// (shared/arena.ts: battle(), arena levels, line-up order) but move no rating
// and pay nothing.
import { ARENA, type ArenaCreature, type BattleResult } from './arena.ts';
import type { BoardRow } from './board.ts';
import { SPECIES } from './species.ts';
import { TRAIT_BY_ID } from './traits.ts';
import { MAX_STARS } from './items.ts';
import { CONFIG } from './config.ts';
import { killCoins } from './xp.ts';
import type { Rolls } from './rolls.ts';
import type { CreatureDTO } from './protocol.ts';

export const SOCIAL = {
  /** Friends per trainer. */
  friendsMax: 100,
  /** Friend requests one trainer can have waiting on others at once. */
  outgoingMax: 20,
  /** Friend requests not answered in this long lapse. */
  requestMs: 7 * 86_400_000,
  /** Blocked plus muted trainers per account. */
  blockMax: 200,
  /** Private messages: the chat limits (200 characters, one every 1.5 s). */
  pmMaxLen: 200,
  pmGapMs: 1500,
  /** Messages kept per pair of trainers (oldest go first). */
  pmKeep: 500,
  /** Starting a conversation with someone who isn't a friend or guildmate (and hasn't messaged you): this trainer level, and at most this many new people per hour. */
  dmMinLevel: 10,
  dmNewPerHour: 10,
  /** Conversations listed in the inbox. */
  inboxMax: 100,
  /** Creatures on a profile's showcase. */
  showcaseMax: 6,
  /** Profile requests one trainer may make per profileWindowMs (hover previews and popups). */
  profileBurst: 20,
  profileWindowMs: 10_000,
  /** The owner-side part of a profile is reused this long (the viewer's relation is never cached). */
  profileCacheMs: 15_000,
  /** A friendly battle invite waits this long for an answer. */
  inviteMs: 5 * 60_000,
  /** The same trainer can be asked again (a friend request, a friendly battle invite) only this long after the last ask, so cancel-and-resend can't spam notes. */
  reaskFriendMs: 10 * 60_000,
  reaskInviteMs: 60_000,
  /** After a friendly battle both trainers wait this long before another one, with anyone. */
  friendlyCooldownMs: 5 * 60_000,
  /** A friendly battle's result and log stay viewable this long. */
  resultKeepMs: 30 * 60_000,
  /** Training: creatures per side, and one battle per account this often. */
  trainingSide: ARENA.teamMax,
  trainingCooldownMs: 5 * 60_000,
  /** Made-up training creatures: level and refine (per stat) caps. */
  testLevelMax: 5000,
  testRefineMax: 500,
} as const;

/**
 * Coin gifts between friends (the reference: 15% burned, at least 1,000 sent).
 * The fee is burned, never paid to anyone: a gift is a taxed transfer and a
 * coin sink. The reference has no daily cap; ours stops alt accounts from
 * feeding a main: each day (UTC) a trainer sends at most an hour's hunting
 * coins at their own level (killCoins(level) × capKills, about an hour of kills),
 * and receives at most the same at the recipient's level, from everyone together.
 */
export const GIFT = {
  /** Smallest gift, in coins sent (before the fee). */
  minCoins: 1000,
  /** Share of each gift burned, in percent (whole numbers keep the math exact). */
  burnPct: 15,
  /** Both trainers need this trainer level. */
  minLevel: 15,
  /** The friendship must be this old. */
  friendMs: 7 * 86_400_000,
  /** Daily cap, sent and received: this many kills' coins at the trainer's level. */
  capKills: 1000,
} as const;

/** Coins burned on a gift of `amount` (rounded up, so a gift never burns less than 15%). */
export const giftFee = (amount: number) => Math.ceil((amount * GIFT.burnPct) / 100);
/** What the friend receives from a gift of `amount`. */
export const giftNet = (amount: number) => amount - giftFee(amount);
/** Coins a trainer of this level can send, or receive, per UTC day. */
export const giftCap = (level: number) => killCoins(Math.max(1, Math.floor(level) || 1)) * GIFT.capKills;
/** A gift amount as typed or sent: a whole number of coins, else null (no fractions, NaN, Infinity or exponents past safe integers). */
export function giftAmount(v: unknown): number | null {
  if (typeof v !== 'number' && typeof v !== 'string') return null;
  if (typeof v === 'string' && !/^\s*\d+\s*$/.test(v)) return null;
  const n = Number(v);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/** How the viewer stands with another trainer. */
export type Relation = 'self' | 'friend' | 'sent' | 'received' | 'none';
export type BlockKind = 'block' | 'mute';

export interface FriendRow {
  name: string; level: number; outfit: string; tag?: string; online: boolean; unread: number;
  /** When coin gifts to this friend open (the friendship is GIFT.friendMs old); in the past = open now. */
  giftAt: number;
  /** When an offline friend's last session ended (absent: online, or not in the last 30 days). */
  seen?: number;
}
export interface FriendRequestRow { id: number; name: string; level: number; outfit: string; at: number }
export interface FriendlyInvite { id: number; from: string; to: string; at: number; expires: number; mine: boolean }
export interface FriendlySummary { id: number; at: number; names: [string, string]; winner: string; ms: number; timedOut: boolean; keepUntil: number }
export interface SocialDTO {
  friends: FriendRow[];
  incoming: FriendRequestRow[];
  outgoing: FriendRequestRow[];
  /** Trainers you blocked or muted. */
  blocked: { name: string; kind: BlockKind }[];
  /** Pending friendly invites to or from you. */
  invites: FriendlyInvite[];
  /** Friendly battles you fought in the last 30 minutes. */
  results: FriendlySummary[];
  /** When you may fight your next friendly battle, and your next training battle (0 = now). */
  friendlyAt: number;
  trainingAt: number;
  /** Coin gifts today (UTC): coins you sent and the most you may send; coins you received and the most you may receive. */
  gift: { sent: number; cap: number; received: number; recvCap: number };
  /** Unread messages in the inbox, and conversations waiting under Requests (shared/social.ts InboxRow). */
  dmUnread: number;
  dmRequests: number;
  /** Who may start a conversation with you. */
  dmFrom: DmFrom;
}

/** Who may start a conversation with you: everyone, friends and guildmates, or friends only. Anyone you messaged first can always answer. */
export type DmFrom = 'all' | 'kin' | 'friends';
export const DM_FROM: DmFrom[] = ['all', 'kin', 'friends'];

/** One conversation in the Messages inbox. request: from someone who isn't a friend or guildmate and you haven't answered. */
export interface InboxRow {
  name: string; level: number; outfit: string; tag?: string; online: boolean;
  last: { from: string; text: string; ts: number };
  unread: number; request: boolean; friend: boolean;
}

export interface PmDTO { id: number; ts: number; from: string; to: string; text: string }

export interface ProfileDTO {
  name: string;
  level: number;
  outfit: string;
  nameColor: string | null;
  guild: { tag: string; name: string } | null;
  /**
   * This season's arena standing; null below the arena's level or before a first match this season. While ranked seasons
   * are on (shared/ranked.ts) `ranked` is set: `ar` is trophies and `tierId` a league id.
   */
  arena: { tier: string; tierId: string; ar: number; wins: number; losses: number; ranked?: true } | null;
  /** The creatures that would fight for them (arena line-up, else hunt team), without their order. */
  lineup: { species: string; level: number; arenaLevel: number; aura: boolean; skin?: string }[];
  /** Creatures they picked to show (up to SOCIAL.showcaseMax), as they are now. */
  showcase: CreatureDTO[];
  relation: Relation;
  /** The friend request between you, when relation is sent or received. */
  requestId?: number;
  blocked: boolean;
  muted: boolean;
  /** Online right now: shown to friends only. */
  online?: boolean;
  /** Trainer XP into the current level (the bar on the popup). */
  xp: number;
  /** The title they wear, and their chat badge id (shared/pass.ts BADGE). */
  title?: string;
  badge?: string;
  /** The one creature walking beside them, as on their showcase; null when none. */
  companion: CreatureDTO | null;
  /** Public totals. Coins are shown; diamonds never are. A part the owner hides comes as zeros and is named in `hidden`. */
  stats: ProfileStats;
  hidden?: CardPrefs;
}

/** Parts of the player card its owner keeps to themselves. Diamonds are never shown at all. */
export interface CardPrefs {
  /** Auras caught and owned, and the top grade owned. */
  rarities?: boolean;
  /** The coin total. */
  coins?: boolean;
}

/** Only the known switches, as true or absent. */
export function cleanCardPrefs(v: unknown): CardPrefs {
  const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  return { ...(o.rarities === true ? { rarities: true } : {}), ...(o.coins === true ? { coins: true } : {}) };
}

/** What a profile shows as stat tiles. topGrade: the best grade among creatures owned, and how many have it. */
export interface ProfileStats {
  species: number; speciesOf: number; catches: number; kills: number; aurasCaught: number;
  aurasOwned: number; owned: number; topGrade: string; topGradeN: number;
  ladderBest: number; ladderWins: number; trophies: number; coins: number;
}

/** A made-up training creature. relicOf: one of your creatures whose relic it carries (optional). */
export interface TestSpec {
  species: string; level: number; aura: boolean; bloodline: number; quality: number;
  ivs: number[]; trait: string; stars: number; refine: number[]; relicOf?: number;
}
/** A training slot: a copy of one of your creatures, or a made-up one; row: its row on the board (absent: defaultRows). */
export type TrainSlot = ({ own: number } | { test: TestSpec }) & { row?: BoardRow };

export type SocialOp =
  | { k: 'friendAdd'; name: string }
  | { k: 'friendAnswer'; id: number; accept: boolean }
  | { k: 'friendCancel'; id: number }
  | { k: 'friendRemove'; name: string }
  | { k: 'block'; name: string; kind: BlockKind; on: boolean }
  | { k: 'pm'; name: string; text: string }
  /** A conversation; replies { t: 'pmThread', name, msgs }. Marks it read. */
  | { k: 'pmThread'; name: string }
  /** Your conversations; replies { t: 'inbox', rows: InboxRow[] }. */
  | { k: 'inbox' }
  /** Take a conversation out of your inbox (a request declined); a new message brings it back. */
  | { k: 'pmHide'; name: string }
  /** Send Admin the last lines of a conversation. */
  | { k: 'pmReport'; name: string }
  /** Who may start a conversation with you. */
  | { k: 'dmFrom'; v: DmFrom }
  /** peek: a hover preview (no Friends view comes back with it). */
  | { k: 'profile'; name: string; peek?: boolean }
  | { k: 'showcase'; ids: number[] }
  /** The creature walking beside your trainer icon; null clears it. */
  | { k: 'companion'; id: number | null }
  /** What your player card hides from others. */
  | { k: 'card'; prefs: CardPrefs }
  | { k: 'invite'; name: string }
  | { k: 'inviteAnswer'; id: number; accept: boolean }
  | { k: 'inviteCancel'; id: number }
  /** A friendly battle's log; replies { t: 'friendlyMatch', id, side, result }. */
  | { k: 'match'; id: number }
  | { k: 'train'; sides: [TrainSlot[], TrainSlot[]] }
  /** Send a friend coins: `amount` leaves your wallet, GIFT.burnPct% of it is burned, the rest reaches them. */
  | { k: 'gift'; name: string; amount: number };

/** Names are matched without case, as at sign-up. */
export const sameName = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/** Clean a private message: trimmed, at most SOCIAL.pmMaxLen characters, empty when nothing is left. */
export function cleanPm(text: unknown): string {
  return String(text ?? '').replace(/\s+/g, ' ').trim().slice(0, SOCIAL.pmMaxLen);
}

/** Whether another friendly battle may start now for a trainer whose last one ended at `last`. */
export const friendlyReady = (last: number, now: number) => last <= 0 || now >= last + SOCIAL.friendlyCooldownMs;
/** Whether a training battle may run now for an account whose last one ran at `last`. */
export const trainingReady = (last: number, now: number) => last <= 0 || now >= last + SOCIAL.trainingCooldownMs;
/** Whether an invite made at `at` can still be answered. */
export const inviteOpen = (at: number, now: number) => now < at + SOCIAL.inviteMs;

const int = (v: unknown, lo: number, hi: number) => {
  const n = Math.floor(Number(v));
  return Number.isFinite(n) ? Math.max(lo, Math.min(hi, n)) : lo;
};

/**
 * A made-up training creature, checked and clamped: a real species, level
 * 1..testLevelMax, a Bloodline tier, quality inside the rolled range, six
 * talents 0-31, a real trait, 0..MAX_STARS stars and refine 0..testRefineMax
 * on the five stats that take it. Returns an error text for what can't be fixed by clamping.
 */
export function checkTestSpec(s: unknown): { ok: true; spec: TestSpec } | { ok: false; why: string } {
  const x = (s ?? {}) as Partial<TestSpec>;
  if (typeof x.species !== 'string' || !Object.hasOwn(SPECIES, x.species)) return { ok: false, why: 'Pick a species for each made-up creature.' };
  const trait = typeof x.trait === 'string' && Object.hasOwn(TRAIT_BY_ID, x.trait) ? x.trait : 'sturdy';
  const q = CONFIG.quality;
  const quality = Math.round(Math.max(q.min, Math.min(q.max, Number(x.quality) || 1)) * 1000) / 1000;
  const ivs = Array.from({ length: 6 }, (_, i) => int(x.ivs?.[i], 0, 31));
  const refine = Array.from({ length: 5 }, (_, i) => int(x.refine?.[i], 0, SOCIAL.testRefineMax));
  const spec: TestSpec = {
    species: x.species, level: int(x.level, 1, SOCIAL.testLevelMax), aura: !!x.aura,
    bloodline: int(x.bloodline, 0, CONFIG.bloodline.length - 1), quality, ivs, trait,
    stars: int(x.stars, 0, MAX_STARS), refine,
  };
  if (x.relicOf !== undefined && x.relicOf !== null) spec.relicOf = int(x.relicOf, 0, Number.MAX_SAFE_INTEGER);
  return { ok: true, spec };
}

/** A checked test spec as an arena fighter; id is negative so it never matches a real creature. */
export function testCreature(spec: TestSpec, id: number, relic: ArenaCreature['relic'] = null): ArenaCreature {
  const rolls: Rolls = { ivs: spec.ivs as Rolls['ivs'], quality: spec.quality, bloodline: spec.bloodline, aura: spec.aura, trait: spec.trait };
  return { id: -id, species: spec.species, level: spec.level, rolls, stars: spec.stars, relic, ...(spec.refine.some((r) => r > 0) ? { refine: spec.refine } : {}) };
}

/** A creature as the arena fights it. */
export const asFighter = (c: CreatureDTO): ArenaCreature => ({ id: c.id, species: c.species, level: c.level, rolls: c.rolls, stars: c.stars, relic: c.relic, ...(c.refine ? { refine: c.refine } : {}), ...(c.tablet ? { tablet: c.tablet } : {}), ...(c.held ? { held: c.held } : {}) });

/** Damage each creature dealt in a battle, by side and line-up place. */
export function damageDealt(r: BattleResult): [number[], number[]] {
  const out: [number[], number[]] = [r.sides[0].team.map(() => 0), r.sides[1].team.map(() => 0)];
  for (const b of r.log) out[b.s][b.a] += b.dmg;
  return out;
}
